import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api, formatDate, formatDateTime } from '../../utils/api'
import '../../styles/pages.css'

function MasterProductDetail() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const [product, setProduct] = useState(null)
  const [brands, setBrands] = useState([])
  const [categoryTree, setCategoryTree] = useState([])

  // Form state
  const [formData, setFormData] = useState({
    model: '',
    brand_id: '',
    description: '',
    category_id: ''
  })
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    loadData()
  }, [id])

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const [productRes, brandsRes, categoriesRes] = await Promise.all([
        api.get(`/api/pim/products/${id}`),
        api.get('/api/pim/brands'),
        api.get('/api/pim/categories/tree')
      ])

      setProduct(productRes)
      setBrands(brandsRes.brands || [])
      setCategoryTree(categoriesRes.categories || [])

      // Initialize form
      setFormData({
        model: productRes.model || '',
        brand_id: productRes.brand?.brand_id?.toString() || '',
        description: productRes.description || '',
        category_id: productRes.category?.category_id?.toString() || ''
      })
      setIsDirty(false)
    } catch (err) {
      console.error('Failed to load product:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function handleChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }))
    setIsDirty(true)
    setSuccess(null)
  }

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const updates = {}
      if (formData.model !== product.model) {
        updates.model = formData.model
      }
      if (formData.brand_id !== product.brand?.brand_id?.toString()) {
        updates.brand_id = parseInt(formData.brand_id)
      }
      if (formData.description !== (product.description || '')) {
        updates.description = formData.description
      }
      if (formData.category_id !== (product.category?.category_id?.toString() || '')) {
        updates.category_id = formData.category_id ? parseInt(formData.category_id) : null
      }

      if (Object.keys(updates).length === 0) {
        setSuccess('No changes to save')
        setIsDirty(false)
        return
      }

      const response = await api.patch(`/api/pim/products/${id}`, updates)
      setProduct(response)
      setFormData({
        model: response.model || '',
        brand_id: response.brand?.brand_id?.toString() || '',
        description: response.description || '',
        category_id: response.category?.category_id?.toString() || ''
      })
      setIsDirty(false)
      setSuccess('Product saved successfully')
    } catch (err) {
      console.error('Failed to save product:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  function handleVariantClick(variantId) {
    navigate(`/pim/variants/${variantId}`)
  }

  // Flatten category tree for select dropdown
  function flattenCategories(nodes, depth = 0, result = []) {
    for (const node of nodes) {
      result.push({
        category_id: node.category_id,
        name: node.name,
        depth,
        displayName: '\u00A0'.repeat(depth * 4) + node.name
      })
      if (node.children?.length > 0) {
        flattenCategories(node.children, depth + 1, result)
      }
    }
    return result
  }

  const flatCategories = flattenCategories(categoryTree)

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  if (!product) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-state-title">Product Not Found</div>
          <div className="empty-state-description">The requested product could not be found.</div>
          <button className="btn btn-primary" onClick={() => navigate('/pim/products')}>
            Back to Products
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Back button */}
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => navigate('/pim/products')}
        style={{ marginBottom: 'var(--space-4)' }}
      >
        &larr; Back to Products
      </button>

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {success && (
        <div className="card" style={{ background: 'var(--color-success-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          {success}
        </div>
      )}

      {/* Product Details Form */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 style={{ marginBottom: 'var(--space-4)' }}>Product Details</h3>
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-4)' }}>
            <div className="filter-group">
              <label className="filter-label">Model Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.model}
                onChange={e => handleChange('model', e.target.value)}
                required
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">Brand *</label>
              <select
                className="form-input"
                value={formData.brand_id}
                onChange={e => handleChange('brand_id', e.target.value)}
                required
              >
                <option value="">Select Brand</option>
                {brands.map(brand => (
                  <option key={brand.brand_id} value={brand.brand_id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Category</label>
              <select
                className="form-input"
                value={formData.category_id}
                onChange={e => handleChange('category_id', e.target.value)}
              >
                <option value="">No Category</option>
                {flatCategories.map(cat => (
                  <option key={cat.category_id} value={cat.category_id}>
                    {cat.displayName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-group" style={{ marginTop: 'var(--space-4)' }}>
            <label className="filter-label">Description</label>
            <textarea
              className="form-input"
              rows={4}
              value={formData.description}
              onChange={e => handleChange('description', e.target.value)}
              placeholder="Enter product description..."
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-border)' }}>
            <div className="text-sm text-muted">
              Created: {formatDateTime(product.created_at)} | Updated: {formatDateTime(product.updated_at)}
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isDirty || saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Variants Table */}
      <div className="card">
        <h3 style={{ marginBottom: 'var(--space-4)' }}>
          Variants ({product.variants?.length || 0})
        </h3>

        {product.variants?.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-description">No variants for this product.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Dimensions</th>
                <th>Created</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {product.variants?.map(variant => (
                <tr
                  key={variant.variant_id}
                  onClick={() => handleVariantClick(variant.variant_id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <span style={{ fontFamily: 'monospace' }}>
                      {variant.sku || '-'}
                    </span>
                  </td>
                  <td>{variant.dimensions_display || 'No dimensions'}</td>
                  <td className="text-sm text-muted">{formatDate(variant.created_at)}</td>
                  <td className="text-sm text-muted">{formatDate(variant.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

export default MasterProductDetail
